<template>
  <div
    class="trezo-card bg-primary-500 mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-content relative ltr:md:pr-[230px] rtl:md:pl-[230px]"
    >
      <div class="md:pt-[5px] md:pb-[5px]">
        <h5 class="mb-[5px] md:mb-[2px] font-semibold text-white">
          {{ directT('dashboard.welcome') }}, <span class="text-[#ffcea9]">{{ userName }}!</span>
        </h5>
        <p class="text-[#d5d9e2]">
          {{ directT('dashboard.whatsHappening') }}
        </p>
        <div
          class="border-t border-primary-400 mt-[15px] mb-[15px] md:mt-[30px] md:mb-[33px]"
        ></div>
        <div class="sm:flex items-center">
          <div
            class="flex items-center ltr:sm:mr-[20px] ltr:2xl:mr-[40px] rtl:sm:ml-[20px] rtl:2xl:ml-[40px]"
          >
            <div
              class="w-[42px] h-[42px] rtl:ml-[12px] ltr:mr-[12px] bg-primary-50 text-primary-500 rounded-md flex items-center justify-center"
            >
              <i class="material-symbols-outlined"> shopping_bag </i>
            </div>
            <div>
              <span
                class="text-[15px] md:text-md text-white block font-semibold mb-[1px] md:mb-0"
              >
                <template v-if="loading">
                  <span class="animate-pulse">...</span>
                </template>
                <template v-else>
                  {{ todayOrdersCount }} {{ directT('dashboard.newOrders') }}
                </template>
              </span>
              <span class="block text-gray-200"> {{ directT('dashboard.awaitingProcessing') }} </span>
            </div>
          </div>
          <div class="flex items-center mt-[15px] sm:mt-0">
            <div
              class="w-[42px] h-[42px] rtl:ml-[12px] ltr:mr-[12px] bg-danger-50 text-danger-500 rounded-md flex items-center justify-center"
            >
              <i class="material-symbols-outlined"> chat_error </i>
            </div>
            <div>
              <span
                class="text-[15px] md:text-md text-white block font-semibold mb-[1px] md:mb-0"
              >
                <template v-if="loading">
                  <span class="animate-pulse">...</span>
                </template>
                <template v-else>
                  {{ lowStockCount }} {{ directT('dashboard.products') }}
                </template>
              </span>
              <span class="block text-gray-200"> {{ directT('dashboard.outOfStock') }} </span>
            </div>
          </div>
        </div>
      </div>
      <div
        class="text-center md:absolute ltr:right-0 rtl:left-0 md:max-w-[208.04px] md:top-1/2 md:-translate-y-1/2 mt-[20px] md:mt-0"
      >
        <img
          src="@/assets/images/welcome.png"
          class="inline-block"
          alt="welcome-image"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { supabase } from "@/lib/supabaseClient";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

// Define the interface for user profile
interface UserProfile {
  id: string;
  user_id: string;
  display_name: string;
  role: string;
  email: string;
}

// Define the interface for products
interface Product {
  id: string;
  stock: number | null;
  [key: string]: any; // Allow other properties
}

export default defineComponent({
  name: "WelcomeDashboard",
  setup() {
    const { locale } = useI18n();
    const userName = ref('User');
    const todayOrdersCount = ref(0);
    const lowStockCount = ref(0);
    const loading = ref(true);

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };

    // Fetch today's orders count
    const fetchTodayOrders = async () => {
      try {
        // Calculate today's start and end dates
        const today = new Date();
        const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
        
        // Get current user's organization
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.warn('User not authenticated');
          todayOrdersCount.value = 0;
          return;
        }
        
        // Get the user's organization ID
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', user.id)
          .single();
        
        if (profileError || !userProfile?.org_id) {
          console.warn('User has no organization');
          todayOrdersCount.value = 0;
          return;
        }
        
        const organizationId = userProfile.org_id as string;
        
        // Fetch today's orders count
        const { data: orders, error } = await supabase
          .from('orders')
          .select('id')
          .eq('organization_id', organizationId)
          .gte('created_at', startOfToday.toISOString())
          .lte('created_at', endOfToday.toISOString());
        
        if (error) {
          console.error('Error fetching today orders:', error);
          todayOrdersCount.value = 0;
          return;
        }
        
        todayOrdersCount.value = orders ? orders.length : 0;
        
        // Fetch low stock products count using separate queries for null and zero stock
        let lowStockProducts: Product[] = [];
        
        // Query for products with stock = 0
        const { data: zeroStockProducts, error: zeroStockError } = await supabase
          .from('products')
          .select('id, stock')
          .eq('org_id', organizationId)
          .eq('stock', 0);
          
        if (zeroStockError) {
          console.error('Error fetching zero stock products:', zeroStockError);
        } else if (zeroStockProducts) {
          lowStockProducts = [...(zeroStockProducts as Product[])];
        }
        
        // Query for products with stock = null
        const { data: nullStockProducts, error: nullStockError } = await supabase
          .from('products')
          .select('id, stock')
          .eq('org_id', organizationId)
          .is('stock', null);
          
        if (nullStockError) {
          console.error('Error fetching null stock products:', nullStockError);
        } else if (nullStockProducts) {
          lowStockProducts = [...lowStockProducts, ...(nullStockProducts as Product[])];
        }
        
        console.log('Products with zero/null stock:', lowStockProducts);
        lowStockCount.value = lowStockProducts.length;
        
      } catch (error) {
        console.error('Error fetching today orders:', error);
        todayOrdersCount.value = 0;
        lowStockCount.value = 0;
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && session.user) {
          // Get user email from auth
          const email = session.user.email || '';
          
          // Get user profile data from user_profiles table
          const { data, error } = await supabase
            .from('user_profiles')
            .select('display_name, email')
            .eq('user_id', session.user.id)
            .single();
          
          if (error) {
            console.error('Error fetching user profile:', error);
            // Fallback to email
            userName.value = email.split('@')[0] || 'User';
          } else {
            const profile = data as UserProfile;
            
            if (profile && profile.display_name) {
              // Use display_name for userName
              userName.value = profile.display_name.split(' ')[0]; // Get first name only
            } else {
              // Fallback to email
              userName.value = email.split('@')[0] || 'User';
            }
          }
          
          // Fetch today's orders and low stock count
          await fetchTodayOrders();
        }
      } catch (error) {
        console.error('Error in WelcomeDashboard:', error);
        loading.value = false;
      }
    });

    return {
      directT,
      userName,
      todayOrdersCount,
      lowStockCount,
      loading
    };
  }
});
</script>
